import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Check, Plus, Search, ShoppingBag } from "lucide-react";
import View from "./View";
import Cart from "./Cart";

const c = {
  greenDk: "#1C4A2E",
  greenMd: "#2D6B45",
  greenLt: "#4A9B6F",
  gold: "#E8A020",
  red: "#E84040",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  surface2: "#E8EDEA",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
};

interface Premio {
  premio_id: number;
  nombre: string;
  puntos?: number;
  imagen?: string;
  descripcion?: string;
  estado: string;
  signedUrl: string;
}

interface CartLine {
  premio: string;
  cantidad: number;
  premio_id?: number;
  cart_premio_id: number;
}

/** Set from Home to open the premios carrito on next visit. */
const OPEN_PREMIOS_CART_KEY = "mispuntos_open_premios_cart";

function formatPts(n: number) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return v.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function emojiForName(name: string) {
  const s = name.trim();
  if (/tablet|celular|phone|tech|laptop|usb|bluetooth|audífon/i.test(s)) return "📱";
  if (/super|vale|mercado|voucher|restaurant|cine|cinema/i.test(s)) return "🛒";
  if (/coffee|café|taza|licuadora|micro|olla|plancha|electro|aspir/i.test(s)) return "☕";
  if (/sábana|toalla|hogar|organiz|colchon|escritorio/i.test(s)) return "🛏️";
  if (/vitamín|spa|deport|bienestar|masaje|kit/i.test(s)) return "💊";
  return "🎁";
}

/** Categoría para filtros (sin campo API); coincide con el mock por palabras clave. */
function bucketId(nombre: string): string {
  const n = nombre.toLowerCase();
  if (/vale|voucher|super|mercado|restaurant|cine|entradas/i.test(n)) return "vales";
  if (/tablet|celular|laptop|usb|bluetooth|audífon|tecn|bocina|cargador/i.test(n)) return "tecnologia";
  if (/licuadora|micro|olla|plancha|caf|electro|arrocera|horno/i.test(n)) return "electrodomesticos";
  if (/sábana|toalla|organiz|hogar|edred/i.test(n)) return "hogar";
  if (/vitamín|spa|deport|bienestar|masaje|yoga|kit/i.test(n)) return "bienestar";
  return "otros";
}

const CAT_PILLS: { id: string; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "affordable", label: "A mi alcance" },
  { id: "electrodomesticos", label: "Electrodomésticos" },
  { id: "tecnologia", label: "Tecnología" },
  { id: "hogar", label: "Hogar" },
  { id: "bienestar", label: "Bienestar" },
];

function categoryLabel(nombre: string): string {
  const id = bucketId(nombre);
  const fromPill = CAT_PILLS.find((p) => p.id === id)?.label;
  if (fromPill) return fromPill;
  if (id === "vales") return "Vales";
  if (id === "otros") return "Otros";
  return "Premio";
}

const Premios = ({
  setView,
}: {
  setView: Dispatch<SetStateAction<string>>;
}) => {
  const [premios, setPremios] = useState<Premio[]>([]);
  const [puntos, setPuntos] = useState(0);
  const [premioView, setPremioView] = useState(false);
  const [premioViewID, setPremioViewID] = useState(0);
  const [cartView, setCartView] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCat, setActiveCat] = useState("todos");
  const [counter, setCounter] = useState(0);
  const [carritoLines, setCarritoLines] = useState<CartLine[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const getCarrito = useCallback(async () => {
    const userId = sessionStorage.getItem("user_id");
    if (!userId) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/carrito/${userId}`,
        {
          headers: { "auth-token": `${sessionStorage.getItem("token")}` },
        }
      );
      const data = res.data;
      setCarritoLines(Array.isArray(data) ? data : []);
    } catch {
      setCarritoLines([]);
    }
  }, []);

  const getPremios = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/premios/activos`,
        {
          headers: {
            "auth-token": `${sessionStorage?.getItem("token")}`,
          },
        }
      );

      const premiosWithSignedUrls = await Promise.all(
        response.data.map(async (premio: any) => {
          let signedUrl = "";
          if (premio.imagen) {
            try {
              const { data: s3Data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent(premio.imagen)}`,
                { headers: { "auth-token": sessionStorage.getItem("token") } }
              );
              signedUrl = s3Data.url;
            } catch {
              signedUrl = "";
            }
          }

          return {
            ...premio,
            signedUrl,
          } as Premio;
        })
      );

      setPremios(premiosWithSignedUrls);
    } catch {
      setPremios([]);
    }
  };

  const getPuntos = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/usuarios/puntos/${sessionStorage?.getItem(
          "user_id"
        )}`,
        {
          headers: {
            "auth-token": `${sessionStorage?.getItem("token")}`,
          },
          params: { user_id: sessionStorage?.getItem("user_id") },
        }
      );
      setPuntos(response.data.puntos);
    } catch {
      setPuntos(0);
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem(OPEN_PREMIOS_CART_KEY) === "1") {
      sessionStorage.removeItem(OPEN_PREMIOS_CART_KEY);
      setCartView(true);
    }
  }, []);

  useEffect(() => {
    getPuntos();
    getPremios();
  }, [counter]);

  useEffect(() => {
    getCarrito();
  }, [counter, getCarrito, cartView]);

  const inCart = useCallback(
    (p: Premio) =>
      carritoLines.some(
        (line) =>
          (line.premio_id != null && line.premio_id === p.premio_id) ||
          (line.premio && line.premio === p.nombre)
      ),
    [carritoLines]
  );

  const cartItemCount = useMemo(
    () => carritoLines.reduce((s, l) => s + (l.cantidad || 0), 0),
    [carritoLines]
  );

  const filteredPremios = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return premios
      .filter((premio) => {
        if (premio.estado && premio.estado.toLowerCase() === "inactivo") {
          return false;
        }
        if (q) {
          const hay = `${premio.nombre} ${premio.descripcion ?? ""} ${categoryLabel(
            premio.nombre
          )}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (activeCat === "todos") return true;
        if (activeCat === "affordable") {
          return (premio.puntos ?? Infinity) <= puntos;
        }
        return bucketId(premio.nombre) === activeCat;
      })
      .sort((a, b) => (a.premio_id ?? 0) - (b.premio_id ?? 0));
  }, [premios, searchTerm, activeCat, puntos]);

  const openDetail = (id: number) => {
    setPremioViewID(id);
    setPremioView(true);
  };

  const premioForDetail = useMemo(
    () => premios.find((p) => p.premio_id === premioViewID),
    [premios, premioViewID]
  );

  const addToCart = async (e: React.MouseEvent, premio: Premio) => {
    e.stopPropagation();
    if (inCart(premio)) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/carrito/${sessionStorage.getItem(
          "user_id"
        )}/${premio.premio_id}`,
        {},
        { headers: { "auth-token": `${sessionStorage.getItem("token")}` } }
      );
      setCounter((c) => c + 1);
      await getCarrito();
      setToast(`¡${premio.nombre} agregado!`);
      window.setTimeout(() => setToast(null), 2200);
    } catch {
      setToast("No se pudo agregar. Intentá de nuevo.");
      window.setTimeout(() => setToast(null), 2500);
    }
  };

  const resultsLabel = useMemo(() => {
    const n = filteredPremios.length;
    if (!searchTerm.trim() && activeCat === "todos") {
      return n === 1 ? "1 premio" : `Todos los premios · ${n} premios`;
    }
    return `${n} premio${n === 1 ? "" : "s"} encontrado${n === 1 ? "" : "s"}`;
  }, [filteredPremios.length, searchTerm, activeCat]);

  return (
    <>
      <div
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden md:rounded-xl md:border md:border-gray-200/90 md:shadow-sm"
        style={{ background: c.surface }}
      >
        <div
          className="flex flex-shrink-0 items-center justify-between border-b px-3 py-2.5 md:px-6 md:py-3"
          style={{ borderColor: c.border, background: c.white }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white"
              style={{
                background: c.gold,
                boxShadow: "0 2px 8px rgba(232,160,32,0.35)",
              }}
              aria-hidden
            >
              ¢
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-extrabold leading-none" style={{ color: c.ink }}>
                {formatPts(puntos)}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="relative flex h-10 w-10 items-center justify-center rounded-[11px] border-0 transition"
            style={{ background: c.surface2 }}
            onClick={() => setCartView((v) => !v)}
            aria-label="Abrir carrito"
          >
            <ShoppingBag className="h-5 w-5" style={{ color: c.ink }} />
            {cartItemCount > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 text-[0.6rem] font-bold text-white"
                style={{ background: c.red, borderColor: c.white }}
              >
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            )}
          </button>
        </div>

        <div
          className="flex-shrink-0 px-3 pb-2 pt-2 md:px-6"
          style={{ background: c.white }}
        >
          <div className="relative">
            <Search
              className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
              style={{ color: c.muted }}
            />
            <input
              type="search"
              placeholder="Buscar premio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border-[1.5px] py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-[#2D6B45]"
              style={{
                borderColor: c.border,
                background: c.surface,
                color: c.ink,
                fontFamily: "inherit",
              }}
            />
          </div>
        </div>

        <div
          className="flex flex-shrink-0 gap-2 overflow-x-auto border-b px-2.5 py-2.5 [scrollbar-width:none] md:flex-wrap md:overflow-x-visible md:px-6 md:py-3 [&::-webkit-scrollbar]:hidden"
          style={{ borderColor: c.border, background: c.white }}
        >
          {CAT_PILLS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActiveCat(p.id)}
              className="flex-shrink-0 rounded-full border-[1.5px] px-2.5 py-1.5 text-[0.7rem] font-semibold transition"
              style={{
                borderColor: activeCat === p.id ? c.greenMd : c.border,
                background: activeCat === p.id ? c.greenMd : "transparent",
                color: activeCat === p.id ? c.white : c.muted,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-3.5 py-2.5 md:px-6"
          style={{ scrollbarGutter: "stable" }}
        >
          <p
            className="mb-2.5 text-[0.65rem] font-semibold uppercase tracking-widest md:text-xs"
            style={{ color: c.muted }}
          >
            {resultsLabel}
          </p>
          {filteredPremios.length === 0 ? (
            <div className="px-2 py-12 text-center" style={{ gridColumn: "1 / -1" }}>
              <div className="mb-3 text-4xl" aria-hidden>
                🔍
              </div>
              <p className="text-sm" style={{ color: c.muted }}>
                No hay premios que coincidan
                <br />
                con tu búsqueda o filtro.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-3 lg:grid-cols-4">
              {filteredPremios.map((premio) => {
                const added = inCart(premio);
                return (
                  <div
                    key={premio.premio_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetail(premio.premio_id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDetail(premio.premio_id);
                      }
                    }}
                    className="flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-white transition hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ borderColor: c.border, borderWidth: 1.5 }}
                  >
                    <div
                      className="relative w-full shrink-0 overflow-hidden"
                      style={{
                        background: "#f0f0f0",
                        aspectRatio: "1 / 1",
                      }}
                    >
                      {premio.signedUrl ? (
                        <img
                          src={premio.signedUrl}
                          alt={premio.nombre}
                          className="h-full w-full p-2.5 sm:p-3"
                          style={{ objectFit: "contain", objectPosition: "center" }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-5xl leading-none" aria-hidden>
                            {emojiForName(premio.nombre)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col gap-1.5 p-2.5">
                      <p
                        className="text-[0.55rem] font-semibold uppercase tracking-widest"
                        style={{ color: c.greenMd }}
                      >
                        {categoryLabel(premio.nombre)}
                      </p>
                      <p
                        className="line-clamp-2 min-h-8 text-[0.8rem] font-bold leading-tight"
                        style={{ color: c.ink }}
                      >
                        {premio.nombre}
                      </p>
                      <div className="mt-auto flex items-center justify-between border-t-0 pt-1">
                        <div
                          className="flex items-center gap-0.5 text-[0.78rem] font-extrabold"
                          style={{ color: c.gold }}
                        >
                          <span
                            className="flex h-4 w-4 items-center justify-center rounded-full text-[0.45rem] text-white"
                            style={{ background: c.gold }}
                          >
                            ¢
                          </span>
                          {formatPts(premio.puntos ?? 0)}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => addToCart(e, premio)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-white transition active:scale-90"
                          style={{ background: added ? c.greenLt : c.greenMd }}
                          aria-label={added ? "En carrito" : "Agregar al carrito"}
                        >
                          {added ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          className="pointer-events-none fixed bottom-20 left-1/2 z-[1000] -translate-x-1/2 flex items-center gap-1.5 rounded-full px-3.5 py-2.5 text-sm font-semibold text-white shadow-lg"
          style={{ background: c.greenDk }}
        >
          {toast}
        </div>
      )}

      <View
        premioView={premioView}
        setPremioView={setPremioView}
        premioViewID={premioViewID}
        onCartChanged={() => {
          setCounter((c) => c + 1);
        }}
        inCart={!!(premioForDetail && inCart(premioForDetail))}
        categoryLabel={categoryLabel}
      />
      <Cart
        counter={counter}
        setCounter={setCounter}
        cartView={cartView}
        setCartView={setCartView}
        availablePoints={puntos}
        setView={setView}
      />
    </>
  );
};

export default Premios;
